import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { ReactNode, useState } from 'react';
import { renderSkeletonCards } from '../../../../components/SkeletonElements';
import { useIotsApi } from '../../../../contexts/iotsApi';
import { useLanguage } from '../../../../contexts/language';
import {
  NewVesselCertificate,
  Vessel,
  VesselCertificate,
} from '../../../../types/vessel';
import DeleteCertificateConfirmationDialog from '../../../../components/DeleteCertificateConfirmationDialog';
import CertificationCard from './CertificationCard';
import {
  ListElement,
  UseDraftListResult,
} from '../../../../hooks/useDraftList';
import useApiErrorMessageGetter from '../../../../hooks/useApiErrorMessage';
import AddButtonCard from '../../../../components/AddButtonCard';

export interface ValidCertificationsProps {
  vessel: Vessel;
  draftListManager: UseDraftListResult<VesselCertificate>;
  emptyContent: ReactNode;
  showAddButton?: boolean;
  areCertificatesPending: boolean;
  isFetchingCertificates: boolean;
  onCertificatesDataUpdate: () => void;
}

const CertificationTabContent = ({
  vessel,
  draftListManager,
  emptyContent,
  showAddButton = false,
  isFetchingCertificates,
  areCertificatesPending,
  onCertificatesDataUpdate,
}: ValidCertificationsProps) => {
  const { t } = useLanguage();
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();
  const { addVesselCertificate, deleteVesselCertificate } = useIotsApi();

  const [certificateListElementToDelete, setCertificateListElementToDelete] =
    useState<ListElement<VesselCertificate> | null>(null);

  const {
    mutate: applyAddCertificateMutation,
    isPending: isAddCertificatePending,
    reset: resetAddCertificateMutation,
  } = useMutation({
    mutationFn: (newCertificate: NewVesselCertificate) =>
      addVesselCertificate(vessel.id, newCertificate),
  });

  const {
    mutate: applyDeleteCertificateMutation,
    isPending: isDeleteCertificatePending,
    reset: resetDeleteCertificateMutation,
  } = useMutation({
    mutationFn: (certificateId: string) =>
      deleteVesselCertificate(vessel.id, certificateId),
  });

  if (areCertificatesPending) {
    return <div className="flex flex-col gap-4">{renderSkeletonCards(3)}</div>;
  }

  const { list, isReady, operations } = draftListManager;

  if (!list.length) {
    return (
      <>
        {emptyContent}
        {showAddButton && (
          <AddButtonCard
            disabled={!isReady}
            onClick={() => operations.addDraftElement()}
          />
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {showAddButton && (
        <AddButtonCard
          disabled={!isReady}
          onClick={() => operations.addDraftElement()}
        />
      )}
      {list.map(item =>
        item.isDraft ? (
          <CertificationCard
            vessel={vessel}
            certificate={null}
            isLoading={isFetchingCertificates || isAddCertificatePending}
            onDelete={hasPendingChanges => {
              if (hasPendingChanges) {
                setCertificateListElementToDelete(item);
              } else {
                operations.removeElement(item);
              }
            }}
            onSave={newCertificate => {
              applyAddCertificateMutation(newCertificate, {
                onSuccess: certificate => {
                  operations.promoteDraftToPersistentElement(
                    item.id,
                    certificate,
                  );
                  enqueueSnackbar({
                    variant: 'success',
                    message: t('content.certificate-creation-success'),
                  });
                  onCertificatesDataUpdate();
                },
                onError: error => {
                  enqueueSnackbar({
                    variant: 'error',
                    message: getErrorMessage(
                      error,
                      t('content.certificate-creation-error'),
                    ),
                  });
                  resetAddCertificateMutation();
                },
              });
            }}
            key={item.id}
          />
        ) : (
          <CertificationCard
            vessel={vessel}
            certificate={item.data}
            isLoading={isFetchingCertificates || isAddCertificatePending}
            onSave={modifiedCertificationValues => {
              // TODO: Implement edition
              console.log(
                'Save changes to certificate',
                modifiedCertificationValues,
              );
            }}
            onDelete={() => {
              setCertificateListElementToDelete(item);
            }}
            key={item.data.id}
            onBaseDataUpdate={newData => {
              operations.updatePersistentElement(item.data, newData);
              onCertificatesDataUpdate();
            }}
          />
        ),
      )}

      <DeleteCertificateConfirmationDialog
        certificateItem={certificateListElementToDelete}
        isLoading={isDeleteCertificatePending}
        onClose={() => {
          if (!isDeleteCertificatePending) {
            setCertificateListElementToDelete(null);
          }
        }}
        onConfirm={() => {
          if (!certificateListElementToDelete) {
            return;
          }
          if (certificateListElementToDelete.isDraft) {
            operations.removeElement(certificateListElementToDelete);
            setCertificateListElementToDelete(null);
            return;
          }

          const { data: certificate } = certificateListElementToDelete;
          applyDeleteCertificateMutation(certificate.id, {
            onSuccess: () => {
              enqueueSnackbar({
                variant: 'success',
                message: t('content.certificate-deletion-success'),
              });
              operations.removeElement(certificateListElementToDelete);
              setCertificateListElementToDelete(null);
              onCertificatesDataUpdate();
            },
            onError: error => {
              enqueueSnackbar({
                variant: 'error',
                message: getErrorMessage(
                  error,
                  t('content.certificate-deletion-error'),
                ),
              });
              resetDeleteCertificateMutation();
            },
          });
        }}
      />
    </div>
  );
};

export default CertificationTabContent;
